import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CreditCard, Download, FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { accountBillingApi } from '@/services/endpoints/account/billing';
import { ACCOUNT_BILLING_KEYS } from '@/components/account/queryKeys';
import AddCardForm from '@/components/account/AddCardForm';
import {
  accountInputClass,
  DataRow,
  EmptyState,
  FieldLabel,
  LoadingRows,
  Notice,
  Panel,
  SectionTitle,
  StatusPill,
  daysUntil,
  formatDay,
  formatMinorMoney,
  formatMoney,
} from '@/components/account/accountUi';
import type { StatusTone } from '@/components/account/accountUi';

function apiMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return axiosError?.response?.data?.message || axiosError?.response?.data?.error || fallback;
}

const INVOICE_TONE: Record<string, StatusTone> = {
  paid: 'good',
  open: 'warn',
  draft: 'neutral',
  void: 'neutral',
  uncollectible: 'bad',
};

function cardLabel(brand: string): string {
  const normalized = brand?.toLowerCase() ?? '';
  if (normalized === 'amex') return 'American Express';
  if (!normalized) return 'Card';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export default function AccountBilling() {
  const queryClient = useQueryClient();
  const [addingCard, setAddingCard] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactTouched, setContactTouched] = useState(false);

  const { data: dashboard } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.dashboard,
    queryFn: accountBillingApi.getDashboard,
    staleTime: 60_000,
  });

  const { data: methods, isLoading: loadingMethods } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.paymentMethods,
    queryFn: accountBillingApi.getPaymentMethods,
    staleTime: 60_000,
  });

  const { data: invoices, isLoading: loadingInvoices } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.invoices,
    queryFn: accountBillingApi.getInvoices,
    staleTime: 60_000,
  });

  const { data: contact } = useQuery({
    queryKey: ACCOUNT_BILLING_KEYS.contact,
    queryFn: accountBillingApi.getContact,
    staleTime: 5 * 60_000,
  });

  // Seed the field once from the server value; typing then owns it.
  useEffect(() => {
    if (contact && !contactTouched) setContactEmail(contact.overrideEmail ?? '');
  }, [contact, contactTouched]);

  const subscription = dashboard?.subscription ?? null;
  const dunning = subscription?.dunning;
  const inTrouble = subscription?.status === 'past_due' || subscription?.status === 'suspended';
  const graceDays = daysUntil(dunning?.graceEndsAt ?? null);

  const invalidateBilling = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.paymentMethods }),
      queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.dashboard }),
      queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.invoices }),
    ]);

  const setDefault = useMutation({
    mutationFn: (paymentMethodId: string) =>
      accountBillingApi.setDefaultPaymentMethod(paymentMethodId),
    onSuccess: async (result) => {
      await invalidateBilling();
      if (result.retriedInvoice?.status === 'paid') {
        toast.success('Default card updated and the outstanding invoice was paid.');
      } else if (result.retriedInvoice?.status === 'failed') {
        toast.error(
          result.retriedInvoice.message ??
            'Default card updated, but the outstanding invoice was declined.'
        );
      } else {
        toast.success('Default card updated.');
      }
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not set that card as default.')),
  });

  const removeCard = useMutation({
    mutationFn: (paymentMethodId: string) => accountBillingApi.removePaymentMethod(paymentMethodId),
    onSuccess: async () => {
      await invalidateBilling();
      toast.success('Card removed.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not remove that card.')),
  });

  const retry = useMutation({
    mutationFn: () => accountBillingApi.retryPayment(),
    onSuccess: async (outcome) => {
      await invalidateBilling();
      if (outcome.status === 'paid') toast.success('Payment went through.');
      else if (outcome.status === 'no_open_invoice') toast.info('There is nothing outstanding.');
      else toast.error(outcome.message ?? 'The payment did not complete.');
    },
    onError: (error) => toast.error(apiMessage(error, 'The card was declined.')),
  });

  const saveContact = useMutation({
    mutationFn: (email: string | null) => accountBillingApi.updateContact(email),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.contact });
      setContactTouched(false);
      toast.success('Billing contact updated.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not update the billing contact.')),
  });

  const handleContactSubmit = (event: FormEvent) => {
    event.preventDefault();
    saveContact.mutate(contactEmail.trim() ? contactEmail.trim() : null);
  };

  return (
    <>
      <SectionTitle
        title="Billing"
        description="Cards on file, invoice history, and where receipts are sent."
      />

      <div className="space-y-6">
        {inTrouble && (
          <Notice
            tone={subscription?.status === 'suspended' ? 'bad' : 'warn'}
            title={
              subscription?.status === 'suspended'
                ? 'Access is suspended after a failed payment'
                : 'The last payment failed'
            }
            action={
              <Button size="sm" onClick={() => retry.mutate()} disabled={retry.isPending}>
                {retry.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                Retry payment
              </Button>
            }
          >
            {graceDays !== null && graceDays > 0
              ? `Add or select a working card within ${graceDays} day${graceDays === 1 ? '' : 's'} to keep access.`
              : 'Add or select a working card, then retry the outstanding invoice.'}
          </Notice>
        )}

        <Panel
          title="Payment methods"
          description="The default card is charged for every renewal."
          action={
            !addingCard ? (
              <Button size="sm" variant="outline" onClick={() => setAddingCard(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add card
              </Button>
            ) : undefined
          }
          bodyClassName={methods?.length || addingCard ? 'px-5 py-4' : 'p-0'}
        >
          {addingCard && (
            <div className="mb-5 rounded-lg border border-border bg-muted/30 p-4">
              <AddCardForm
                onDone={() => {
                  setAddingCard(false);
                  toast.success('Card saved.');
                }}
                onCancel={() => setAddingCard(false)}
              />
            </div>
          )}

          {loadingMethods ? (
            <LoadingRows rows={2} />
          ) : methods && methods.length > 0 ? (
            <ul className="divide-y divide-border">
              {methods.map((method) => (
                <li
                  key={method.id}
                  className="flex flex-wrap items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/40">
                      <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">
                        {cardLabel(method.brand)}{' '}
                        <span className="font-mono tabular-nums text-muted-foreground">
                          •••• {method.last4}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Expires{' '}
                        <span className="font-mono tabular-nums">
                          {String(method.expMonth).padStart(2, '0')}/{method.expYear}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.isDefault ? (
                      <StatusPill tone="good" className="px-2 py-0.5">
                        Default
                      </StatusPill>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDefault.mutate(method.id)}
                        disabled={setDefault.isPending}
                      >
                        Make default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove card ending ${method.last4}`}
                      onClick={() => removeCard.mutate(method.id)}
                      disabled={removeCard.isPending || method.isDefault}
                      title={
                        method.isDefault
                          ? 'Set another card as default before removing this one'
                          : undefined
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            !addingCard && (
              <EmptyState
                icon={CreditCard}
                title="No card on file"
                body="Add a card so renewals do not interrupt access."
                action={
                  <Button size="sm" onClick={() => setAddingCard(true)}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    Add card
                  </Button>
                }
              />
            )
          )}
        </Panel>

        <Panel title="Invoices" bodyClassName="p-0">
          {loadingInvoices ? (
            <div className="p-5">
              <LoadingRows rows={3} />
            </div>
          ) : invoices && invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th scope="col" className="px-5 py-3 font-semibold">Invoice</th>
                    <th scope="col" className="px-5 py-3 font-semibold">Date</th>
                    <th scope="col" className="px-5 py-3 font-semibold">Status</th>
                    <th scope="col" className="px-5 py-3 text-right font-semibold">Amount</th>
                    <th scope="col" className="px-5 py-3 text-right font-semibold">
                      <span className="sr-only">Download</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-5 py-3 font-mono text-xs tabular-nums">
                        {invoice.number ?? invoice.id}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                        {formatDay(invoice.created)}
                      </td>
                      <td className="px-5 py-3">
                        <StatusPill
                          tone={INVOICE_TONE[invoice.status] ?? 'neutral'}
                          className="px-2 py-0.5 capitalize"
                        >
                          {invoice.status}
                        </StatusPill>
                      </td>
                      <td className="px-5 py-3 text-right font-mono tabular-nums">
                        {formatMinorMoney(invoice.amountDue, invoice.currency)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {invoice.invoicePdf || invoice.hostedInvoiceUrl ? (
                          <a
                            href={invoice.invoicePdf ?? invoice.hostedInvoiceUrl ?? '#'}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-cyan hover:underline"
                          >
                            <Download className="h-3.5 w-3.5" aria-hidden="true" />
                            PDF
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={FileText}
              title="No invoices yet"
              body="Your first invoice appears here once a billing period closes."
            />
          )}
        </Panel>

        <Panel
          title="Billing contact"
          description="Receipts, renewal reminders, and failed-payment notices go to this address. Leave it empty to use the organization owner's email."
        >
          <form onSubmit={handleContactSubmit} className="max-w-md space-y-4">
            <div>
              <FieldLabel htmlFor="billing-contact">Billing email</FieldLabel>
              <input
                id="billing-contact"
                type="email"
                className={accountInputClass}
                value={contactEmail}
                placeholder={contact?.email ?? 'owner@company.com'}
                onChange={(event) => {
                  setContactTouched(true);
                  setContactEmail(event.target.value);
                }}
              />
              {contact && !contact.overrideEmail && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Currently sent to the owner ({contact.email}).
                </p>
              )}
            </div>
            <Button type="submit" size="sm" disabled={saveContact.isPending || !contactTouched}>
              {saveContact.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              Save billing contact
            </Button>
          </form>
        </Panel>

        {dashboard?.upcomingInvoice && (
          <Panel title="Next invoice" bodyClassName="px-5 py-1">
            <dl>
              <DataRow
                label="Scheduled"
                value={formatDay(dashboard.upcomingInvoice.dueDate)}
                mono
              />
              <DataRow
                label="Amount"
                value={formatMoney(dashboard.upcomingInvoice.amount, subscription?.currency ?? 'cad')}
                mono
              />
            </dl>
          </Panel>
        )}
      </div>
    </>
  );
}
